import React, { useState, useEffect, useRef } from 'react';
import { Text as UIText, Heading } from './ui';

const WalkthroughModal = ({ 
  isOpen, 
  onClose, 
  onStart, 
  onEnd,
  onStop,
  title, 
  message, 
  subMessage, 
  showChef = true,
  chefState = 'talking',
  showStartButton = true,
  startButtonText = "Let's Start!",
  showEndButton = false,
  endButtonText = "Got it!",
  showSecondButton = false,
  secondButtonText = "",
  onSecondButton,
  showImage = false,
  imageUrl = '',
  showAudioExample = false,
  audioUrl = '',
  onPlayAudio,
  audioPlaying = false,
  showStopButton = true,
  showDoNotShowAgain = false,
  className = ""
}) => {
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen && message) {
      setDisplayText('');
      setIsTyping(true);
      setDoNotShowAgain(false); // Reset checkbox when modal opens
      
      let currentIndex = 0;
      const typeInterval = setInterval(() => {
        if (currentIndex < message.length) {
          setDisplayText(message.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          setIsTyping(false);
          clearInterval(typeInterval);
        }
      }, 30); // Typing speed

      return () => clearInterval(typeInterval);
    }
  }, [isOpen, message]);

  // Drag functionality
  const handleMouseDown = (e) => {
    if (e.target.closest('button')) return; // Don't drag if clicking buttons
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  if (!isOpen) return null;

  const getChefImage = () => {
    // Use different chef images based on state
    const chefImages = {
      talking: '/images/Chef-talking.png',
      thinking: '/images/Chef-thinking.png',
      listening: '/images/Chef-listening.png',
      sad: '/images/Chef-sad.png',
      indifferent: '/images/Chef-indifferent.png',
      preFire: '/images/chef_pre_fire.png',
      fire: '/images/chef_fire.png',
      default: '/images/Chef.png'
    };
    return chefImages[chefState] || chefImages.default;
  };

  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop itself, not the modal content
    if (e.target === e.currentTarget) {
      if (showStopButton && onStop) {
        onStop(doNotShowAgain);
      } else if (onClose) {
        onClose(doNotShowAgain);
      }
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div 
        ref={modalRef}
        className={`bg-neutral-0 hairline p-8 max-w-lg mx-4 relative cursor-move select-none ${isDragging ? 'cursor-grabbing' : ''} ${className}`}
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          transition: isDragging ? 'none' : 'transform 0.2s ease-out'
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Close/Stop button */}
        {showStopButton && onStop && (
          <button 
            onClick={() => onStop(doNotShowAgain)}
            className="absolute top-4 right-4 text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0 modal-close"
            title="Stop walkthrough"
          >
            ✕
          </button>
        )}
        {!showStopButton && onClose && (
          <button 
            onClick={() => onClose(doNotShowAgain)}
            className="absolute top-4 right-4 text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0"
          >
            ✕
          </button>
        )}

        {/* Single content area with chef and speech bubble */}
        <div className="text-center">
          {title && (
            <Heading level={3} className="text-neutral-900 mb-4">
              {title}
            </Heading>
          )}
          
          {/* Chef with speech bubble and button */}
          {showChef && (
            <div className="flex justify-center items-end mb-6">
              <div className="relative flex items-end">
                {/* Speech bubble above chef - moved higher to avoid chef photo and other content */}
                <div className="absolute -top-52 left-1/2 transform -translate-x-1/2 bg-neutral-0 hairline p-4 w-80 h-[120px] flex flex-col justify-center">
                  <div className="text-sm text-neutral-900 leading-relaxed h-[60px] flex items-center justify-center text-center">
                    {displayText}
                    {isTyping && <span className="animate-pulse ml-1">|</span>}
                  </div>
                  {/* Speech bubble tail */}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-neutral-900"></div>
                </div>
                
                {/* Chef character */}
                <img 
                  src={getChefImage()} 
                  alt="Chef Character" 
                  className="w-20 h-20 object-contain"
                />
                
                {/* Action button on the right of chef */}
                {showStartButton && !isTyping && (
                  <div className="ml-4 mb-2">
                    <button
                      onClick={() => {
                        if (showDoNotShowAgain && doNotShowAgain && onStop) {
                          onStop(doNotShowAgain);
                        } else {
                          onStart();
                        }
                      }}
                      className="px-6 py-3 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity duration-120"
                    >
                      {startButtonText}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Text content - only show if chef is not visible */}
          {!showChef && (
            <div className="mb-6 min-h-[120px] flex flex-col justify-center">
              <div className="text-neutral-900 leading-relaxed min-h-[60px] flex items-center justify-center">
                {displayText}
                {isTyping && <span className="animate-pulse ml-1">|</span>}
              </div>
              
              {subMessage && !isTyping && (
                <UIText size="sm" className="text-neutral-900 opacity-60 mt-2">
                  {subMessage}
                </UIText>
              )}
            </div>
          )}

          {/* Sub message for chef modal */}
          {showChef && subMessage && !isTyping && (
            <div className="mb-6">
              <UIText size="sm" className="text-neutral-900 opacity-60">
                {subMessage}
              </UIText>
            </div>
          )}

          {/* Image display */}
          {showImage && imageUrl && !isTyping && (
            <div className="mb-6 flex justify-center">
              <img 
                src={imageUrl} 
                alt="Walkthrough illustration" 
                className="max-w-full h-auto max-h-64 object-contain hairline"
              />
            </div>
          )}

          {/* Audio example */}
          {showAudioExample && !isTyping && (
            <div className="mb-6 flex flex-col items-center">
              <button
                onClick={onPlayAudio}
                className="px-6 py-3 bg-neutral-50 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-900 hover:text-neutral-0 transition-colors mb-2"
              >
                {audioPlaying ? '⏸ PAUSE AUDIO' : '▶ PLAY AUDIO EXAMPLE'}
              </button>
              <UIText size="sm" className="text-neutral-900 opacity-60 text-center">
                Listen to hear how to use "Hey Chef"
              </UIText>
            </div>
          )}

          {/* Action buttons for non-chef modal */}
          {!showChef && showStartButton && !isTyping && (
            <div className="mt-6">
              <button
                onClick={onStart}
                className="px-6 py-3 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity duration-120"
              >
                {startButtonText}
              </button>
            </div>
          )}

          {/* End button */}
          {showEndButton && !isTyping && (
            <div className="mt-6 flex gap-4 justify-center">
              {showSecondButton && onSecondButton && (
                <button
                  onClick={onSecondButton}
                  className="px-6 py-3 bg-neutral-50 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-900 hover:text-neutral-0 transition-all duration-120"
                >
                  {secondButtonText}
                </button>
              )}
              <button
                onClick={() => {
                  const handler = onEnd || onClose;
                  if (handler) {
                    handler(doNotShowAgain);
                  }
                }}
                className="px-6 py-3 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity duration-120"
              >
                {endButtonText}
              </button>
            </div>
          )}

          {/* Do Not Show Again Checkbox - Always visible at bottom, smaller font, under all elements */}
          {showChef && showDoNotShowAgain && (
            <div className="flex justify-center mt-6 pt-4 border-t" style={{ borderColor: 'rgba(13, 13, 13, 0.1)' }}>
              <label className="checkbox-label flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={doNotShowAgain}
                  onChange={(e) => setDoNotShowAgain(e.target.checked)}
                  className="mt-0"
                />
                <span className="text-[10px] text-neutral-900 font-mono uppercase tracking-wider opacity-80">
                  Do not show me this message again
                </span>
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WalkthroughModal;
